/**
 * Pure resolution of the context-aware action list for a `WidgetCard` kebab
 * menu. Kept React-free (no JSX) so the filtering rule is unit-tested in the
 * `node --test` sweep; `widget-card.tsx` re-exports the type + resolver and
 * renders the result.
 */

export interface WidgetCardAction {
  id: string;
  label: string;
  onSelect: () => void;
  /**
   * Disable the action (greyed, not selectable) without hiding it — use for
   * actions invalid in the current state (e.g. Export while loading). Pair with
   * `disabledReason` so the operator sees *why* it can't be used.
   */
  disabled?: boolean;
  /**
   * Tooltip explaining why the action is disabled. Surfaced on hover/focus so
   * the operator isn't left guessing. Only rendered when `disabled` is true.
   */
  disabledReason?: string;
  /**
   * Show the action at all. Defaults to visible. Set `false` to hide actions
   * the operator lacks permission for, so the menu never offers something that
   * silently 403s on select. Pattern: `visible: hasPermission`,
   * `disabled: state === 'loading'`.
   */
  visible?: boolean;
}

/**
 * Resolve the action list shown in a `WidgetCard` kebab menu, applying the
 * context-aware filtering rules so each module declares intent once instead of
 * pre-filtering by hand (which leaked actions that then silently 403'd):
 *
 *  - `Refresh` is prepended when `onRefresh` is wired.
 *  - Actions with `visible === false` are dropped entirely (no permission →
 *    not offered).
 *  - Actions with `disabled` are kept but stay greyed; `disabledReason`
 *    explains why on hover.
 */
export function resolveWidgetCardMenu(
  actions: WidgetCardAction[] | undefined,
  onRefresh: (() => void) | undefined
): WidgetCardAction[] {
  const items: WidgetCardAction[] = [];
  if (onRefresh) {
    items.push({ id: 'refresh', label: 'Refresh', onSelect: onRefresh });
  }
  if (actions) {
    items.push(...actions.filter((a) => a.visible !== false));
  }
  return items;
}
